import { useCallback, useEffect, useRef, useState } from "react";

export type BookSort = "name" | "nameReversed" | "createdAt" | "createdAtReversed";

export interface BooksPageState {
  query: string;
  sort: BookSort;
  filterPublic: boolean | null;
  scrollTop: number;
}

const STORAGE_KEY = "publisher_books_page_state";

const DEFAULT_STATE: BooksPageState = {
  query: "",
  sort: "name",
  filterPublic: null,
  scrollTop: 0,
};

function loadSavedState(): BooksPageState {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_STATE;
    const parsed = JSON.parse(raw);
    return {
      query: typeof parsed.query === "string" ? parsed.query : DEFAULT_STATE.query,
      sort: parsed.sort || DEFAULT_STATE.sort,
      filterPublic: parsed.filterPublic !== undefined ? parsed.filterPublic : DEFAULT_STATE.filterPublic,
      scrollTop: typeof parsed.scrollTop === "number" ? parsed.scrollTop : 0,
    };
  } catch {
    return DEFAULT_STATE;
  }
}

function saveState(state: BooksPageState) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Ignore storage quota or security errors
  }
}

export function useBooksPageState(hasData: boolean) {
  const saved = useRef(loadSavedState()).current;

  const [query, setQueryInternal] = useState(saved.query);
  const [sort, setSortInternal] = useState<BookSort>(saved.sort);
  const [filterPublic, setFilterPublicInternal] = useState<boolean | null>(saved.filterPublic);

  const currentScrollTop = useRef(saved.scrollTop);
  const isRestored = useRef(false);

  // When user actively changes query/filters, reset scroll to top
  const setQuery = useCallback((q: string) => {
    setQueryInternal(q);
    currentScrollTop.current = 0;
  }, []);

  const setSort = useCallback((s: BookSort) => {
    setSortInternal(s);
    currentScrollTop.current = 0;
  }, []);

  const setFilterPublic = useCallback((f: boolean | null) => {
    setFilterPublicInternal(f);
    currentScrollTop.current = 0;
  }, []);

  // Sync state to sessionStorage whenever filters or query change
  useEffect(() => {
    saveState({
      query,
      sort,
      filterPublic,
      scrollTop: currentScrollTop.current,
    });
  }, [query, sort, filterPublic]);

  // Track scroll position and restore when returning with data
  useEffect(() => {
    const mainEl = document.getElementById("cabinet-main-content") || document.querySelector("main");
    if (!mainEl) return;

    const onScroll = () => {
      currentScrollTop.current = mainEl.scrollTop;
    };

    mainEl.addEventListener("scroll", onScroll, { passive: true });

    // Restore scroll position once data is available
    if (hasData && !isRestored.current && saved.scrollTop > 0) {
      isRestored.current = true;
      requestAnimationFrame(() => {
        if (mainEl) {
          mainEl.scrollTop = saved.scrollTop;
        }
        setTimeout(() => {
          if (mainEl && Math.abs(mainEl.scrollTop - saved.scrollTop) > 10) {
            mainEl.scrollTop = saved.scrollTop;
          }
        }, 50);
      });
    }

    return () => {
      mainEl.removeEventListener("scroll", onScroll);
      saveState({
        query,
        sort,
        filterPublic,
        scrollTop: currentScrollTop.current,
      });
    };
  }, [hasData, query, sort, filterPublic, saved.scrollTop]);

  return {
    query,
    setQuery,
    sort,
    setSort,
    filterPublic,
    setFilterPublic,
  };
}
